/**
 * @file activity-template.schema.ts
 * @description Define o schema Zod para a entidade 'ActivityTemplate'.
 * Este é o catálogo mestre de tipos de atividades, que serve como um molde
 * para as atividades que serão criadas para as usuárias. É gerenciado por um admin.
 */

import { z } from 'zod';

import { ProviderTypeEnum } from './medical-provider.schema';

// --- Helper Schemas & Enums (de dentro para fora) ---

// -- Seção: Questionnaire Flow --
const AnswerOptionsSchema = z.object({
    options: z.array(z.object({ id: z.string(), text: z.string() })),
  });
  
  const AnswerFreeTextSchema = z.object({
    freeText: z.object({ limit: z.number().int() }),
  });
  
  const AnswerTypeSchema = z.union([AnswerOptionsSchema, AnswerFreeTextSchema]);
  
  const QuestionSchema = z.object({
    index: z.number().int(),
    id: z.string(),
    required: z.boolean(),
    condition: z.any().nullable(),
    valueSync: z.any().nullable(),
    header: z.string(),
    subHeader: z.string().nullable(),
    answer: AnswerTypeSchema,
  });
  
  // -- Seção: FlowType (União de diferentes fluxos) --
  const FlowTypeSchema = z.union([
    z.object({
      scheduling: z.object({
        with: ProviderTypeEnum,
        objectiveName: z.string(),
        collectFeedback: z.boolean(),
      }),
    }),
    z.object({
      questionnaire: z.object({
        questions: z.array(QuestionSchema),
        scoring: z.any().nullable(),
      }),
    }),
  ]);
  
  // -- Seção: Metadados do Template --
  const NewActivitySchema = z.object({
    title: z.string(),
    userAllowed: z.boolean(),
  });
  
  const CalendarDisplayEnum = z.enum(['DATE_WITH_REQUIRED_HOUR', 'DATE_WITH_OPTIONAL_HOUR', 'DATE_ONLY']);
  
  const CalendarSchema = z.object({
    datetimeDisplay: CalendarDisplayEnum,
  });
  
  const CtaSchema = z.object({
    message: z.string(),
    buttonTitle: z.string(),
  });

// --- Main ActivityTemplate Schema ---

const ActivityTemplateSchema = z.object({
  /**
   * O título principal do template da atividade, que será exibido em listas e cabeçalhos.
   * Ex: "Consulta com Médica Obstetra".
   */
  title: z.string(),

  /**
   * Um identificador de string para o ícone a ser exibido na interface.
   * O frontend usará este nome para buscar o componente de ícone correspondente.
   * Ex: "obstetrician", "questionnaire".
   */
  icon: z.string(),

  /**
   * Define a ação ou fluxo principal que esta atividade desencadeia no aplicativo.
   * É uma união que determina qual interface e lógica apresentar ao usuário.
   * Ex: um fluxo de agendamento (`scheduling`) ou um de questionário (`questionnaire`).
   */
  flowType: FlowTypeSchema,

  /**
   * A categoria à qual esta atividade pertence. Usada para agrupar e organizar
   * as atividades na interface do usuário.
   * Ex: "Profissionais", "Exames e Procedimentos".
   */
  category: z.string(),

  /**
   * Define as propriedades para a criação de uma nova atividade a partir deste template.
   * Controla o que é mostrado quando o usuário clica no botão "Adicionar Atividade".
   */
  newActivity: NewActivitySchema,

  /**
   * Define como uma instância desta atividade deve ser tratada e exibida no calendário
   * da usuária (se deve ou não mostrar a hora, por exemplo).
   */
  calendar: CalendarSchema,

  /**
   * Flag que determina se as instâncias desta atividade podem ser recorrentes.
   * Se 'false', a UI não deve oferecer ao usuário a opção de criar uma recorrência.
   */
  allowRecurrence: z.boolean(),

  /**
   * Define a chamada para ação (Call to Action) que pode aparecer na tela inicial
   * ou em um dashboard, incentivando a usuária a interagir com a atividade.
   */
  cta: CtaSchema,
  
  /**
   * Campos de auditoria para rastrear a criação e modificação do template.
   */
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),
});

// --- Inferred TypeScript Types ---

type ActivityTemplate = z.infer<typeof ActivityTemplateSchema>;
type FlowType = z.infer<typeof FlowTypeSchema>;
type Question = z.infer<typeof QuestionSchema>;
type AnswerType = z.infer<typeof AnswerTypeSchema>;
type CalendarDisplay = z.infer<typeof CalendarDisplayEnum>;
type Cta = z.infer<typeof CtaSchema>;
type NewActivity = z.infer<typeof NewActivitySchema>;


// --- Public Exports ---

// Exportando os valores (Zod schemas e enums)
export {
  ActivityTemplateSchema,
  AnswerOptionsSchema,
  AnswerFreeTextSchema,
  AnswerTypeSchema,
  QuestionSchema,
  FlowTypeSchema,
  NewActivitySchema,
  CalendarDisplayEnum,
  CalendarSchema,
  CtaSchema,
};

// Exportando os tipos para uso na aplicação
export type {
  ActivityTemplate,
  FlowType,
  Question,
  AnswerType,
  CalendarDisplay,
  Cta,
  NewActivity,
};